import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { RequestLettersSection } from '../components/RequestLettersSection';
import {
  Spinner, Question, Sparkle, Plus, Trash, PencilSimple, User, Tag,
  CaretDown, Robot, UserCircle, Target, Check, X,
} from '@phosphor-icons/react';

const IMPORTANCE_META = {
  high: { label: 'High', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', order: 0 },
  medium: { label: 'Medium', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', order: 1 },
  low: { label: 'Low', color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400', order: 2 },
};

const STATUS_META = {
  open: { label: 'Open', color: 'bg-white text-[#6B7280] border-[#E5E7EB]' },
  asked: { label: 'Asked', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  partially_answered: { label: 'Partially answered', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  answered: { label: 'Answered', color: 'bg-green-50 text-green-700 border-green-200' },
  resolved: { label: 'Resolved', color: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};
const STATUS_ORDER = ['open', 'asked', 'partially_answered', 'answered', 'resolved'];

const ANSWERABLE_META = {
  likely: { label: 'Likely answerable', color: 'text-green-600' },
  maybe: { label: 'Maybe answerable', color: 'text-amber-600' },
  unlikely: { label: 'Unlikely answerable', color: 'text-red-600' },
};

const IMPORTANCE_GROUPS = ['high', 'medium', 'low'];

const StatusDropdown = ({ question, onChange }) => {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[question.status] || STATUS_META.open;
  return (
    <div className="relative">
      <button data-testid={`oq-status-btn-${question._id}`} onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1 text-[11px] font-ibm font-semibold px-2 py-0.5 rounded border ${meta.color}`}>
        {meta.label} <CaretDown size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-20 bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1 w-44" data-testid={`oq-status-menu-${question._id}`}>
            {STATUS_ORDER.map(s => (
              <button key={s} data-testid={`oq-status-opt-${s}-${question._id}`}
                onClick={() => { setOpen(false); if (s !== question.status) onChange(question._id, s); }}
                className={`w-full text-left px-3 py-1.5 text-xs font-ibm hover:bg-[#F3F4F6] ${s === question.status ? 'font-semibold text-[#002FA7]' : 'text-[#6B7280]'}`}>
                {STATUS_META[s].label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const QuestionCard = ({ question, onStatus, onDelete, onSaveEdit }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(question.text);
  const imp = IMPORTANCE_META[question.importance] || IMPORTANCE_META.medium;
  const ans = ANSWERABLE_META[question.answerable];

  const save = () => {
    const t = text.trim();
    if (t && t !== question.text) onSaveEdit(question._id, t);
    setEditing(false);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`oq-${question._id}`}>
      <div className="flex items-start gap-2">
        {question.source === 'ai'
          ? <Robot size={16} className="text-[#002FA7] mt-0.5 shrink-0" weight="fill" title="AI-generated" />
          : <UserCircle size={16} className="text-[#6B7280] mt-0.5 shrink-0" weight="fill" title="Added by you" />}
        {editing ? (
          <div className="flex-1">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
              data-testid={`oq-edit-input-${question._id}`}
              className="w-full text-sm font-ibm border border-[#E5E7EB] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#002FA7]" />
            <div className="flex items-center gap-2 mt-1.5">
              <button onClick={save} data-testid={`oq-edit-save-${question._id}`} className="flex items-center gap-1 text-xs font-ibm font-semibold text-[#002FA7] hover:underline"><Check size={12} /> Save</button>
              <button onClick={() => { setText(question.text); setEditing(false); }} className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:underline"><X size={12} /> Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-sm font-ibm text-[#09090B] leading-relaxed flex-1" data-testid={`oq-text-${question._id}`}>{question.text}</p>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`text-[10px] font-ibm font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${imp.color}`} data-testid={`oq-importance-${question._id}`}>{imp.label}</span>
          <StatusDropdown question={question} onChange={onStatus} />
        </div>
      </div>

      {question.rationale && (
        <p className="text-[11px] text-[#6B7280] font-ibm italic pl-6 mt-1.5">{question.rationale}</p>
      )}

      <div className="flex items-center gap-3 mt-2 flex-wrap text-[11px] text-[#6B7280] font-ibm pl-6">
        {question.category && question.category !== 'other' && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#F3F4F6] uppercase tracking-wider font-semibold text-[10px]"><Tag size={10} /> {question.category.replace(/_/g, ' ')}</span>
        )}
        {question.recipient && <span className="flex items-center gap-1"><User size={11} /> Ask: {question.recipient}</span>}
        {ans && <span className={`flex items-center gap-1 ${ans.color}`}><Target size={11} /> {ans.label}</span>}
      </div>

      {question.evidence_likely && (
        <p className="text-[11px] text-[#94A3B8] font-ibm pl-6 mt-1">Answered by: {question.evidence_likely}</p>
      )}

      <div className="flex items-center gap-3 mt-3 pl-6">
        {!editing && (
          <button onClick={() => setEditing(true)} data-testid={`oq-edit-${question._id}`} className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-[#002FA7]"><PencilSimple size={12} /> Edit</button>
        )}
        <button onClick={() => onDelete(question._id)} data-testid={`oq-delete-${question._id}`} className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-red-600"><Trash size={12} /> Delete</button>
      </div>
    </div>
  );
};

const AddQuestionForm = ({ onAdd, saving }) => {
  const [text, setText] = useState('');
  const [importance, setImportance] = useState('medium');
  const [recipient, setRecipient] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onAdd({ text: t, importance, recipient: recipient.trim() });
    setText(''); setRecipient(''); setImportance('medium');
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid="oq-add-form">
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Add your own open question…"
        data-testid="oq-add-text"
        className="w-full text-sm font-ibm border border-[#E5E7EB] rounded-md px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
      <div className="flex items-center gap-2 flex-wrap">
        <select value={importance} onChange={e => setImportance(e.target.value)} data-testid="oq-add-importance"
          className="text-xs font-ibm border border-[#E5E7EB] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#002FA7]">
          <option value="high">High importance</option>
          <option value="medium">Medium importance</option>
          <option value="low">Low importance</option>
        </select>
        <input value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="Who can answer? (optional)"
          data-testid="oq-add-recipient"
          className="text-xs font-ibm border border-[#E5E7EB] rounded-md px-2 py-1.5 focus:outline-none focus:border-[#002FA7] flex-1 min-w-[180px]" />
        <button type="submit" disabled={saving || !text.trim()} data-testid="oq-add-submit"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-xs hover:bg-[#00237D] disabled:opacity-50 transition-all">
          {saving ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} />} Add
        </button>
      </div>
    </form>
  );
};

export const OpenQuestionsTab = ({ caseId }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/open-questions`);
      setQuestions(data.open_questions || []);
    } catch (err) {
      logger.error('failed to load open questions:', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post(`/cases/${caseId}/open-questions/generate`);
      await load();
    } catch (err) {
      logger.error('failed to generate open questions:', err);
      alert('Failed to generate questions. Make sure documents have been analyzed first.');
    } finally {
      setGenerating(false);
    }
  };

  const addQuestion = async (payload) => {
    setAdding(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/open-questions`, payload);
      setQuestions(prev => [data.open_question, ...prev]);
      setShowAdd(false);
    } catch (err) {
      logger.error('failed to add question:', err);
      alert('Failed to add question.');
    } finally {
      setAdding(false);
    }
  };

  const setStatus = async (qid, status) => {
    setQuestions(prev => prev.map(q => (q._id === qid ? { ...q, status } : q)));
    try {
      await api.patch(`/cases/${caseId}/open-questions/${qid}`, { status });
    } catch (err) {
      logger.error('failed to update status:', err);
      load();
    }
  };

  const saveEdit = async (qid, text) => {
    setQuestions(prev => prev.map(q => (q._id === qid ? { ...q, text } : q)));
    try {
      await api.patch(`/cases/${caseId}/open-questions/${qid}`, { text });
    } catch (err) {
      logger.error('failed to edit question:', err);
      load();
    }
  };

  const remove = async (qid) => {
    const prev = questions;
    setQuestions(p => p.filter(q => q._id !== qid));
    try {
      await api.delete(`/cases/${caseId}/open-questions/${qid}`);
    } catch (err) {
      logger.error('failed to delete question:', err);
      setQuestions(prev);
    }
  };

  const grouped = IMPORTANCE_GROUPS.map(imp => ({
    imp,
    items: questions.filter(q => (q.importance || 'medium') === imp),
  })).filter(g => g.items.length > 0);

  if (loading) {
    return <div className="flex items-center justify-center py-20" data-testid="open-questions-tab"><Spinner size={28} className="animate-spin text-[#002FA7]" /></div>;
  }

  return (
    <div data-testid="open-questions-tab" className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-[#09090B] flex items-center gap-2">
            <Question size={20} className="text-[#002FA7]" weight="fill" /> Open Questions Register
          </h3>
          <p className="text-sm text-[#6B7280] font-ibm mt-0.5 max-w-2xl">
            The unanswered questions whose answers would most advance your case — so you can ask the right people the right things, once, instead of scattered repeated requests.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowAdd(s => !s)} data-testid="oq-toggle-add"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E7EB] text-[#09090B] font-ibm font-semibold rounded-md text-sm hover:bg-[#F3F4F6] transition-all">
            <Plus size={14} /> Add question
          </button>
          <button onClick={generate} disabled={generating} data-testid="oq-generate-button"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all">
            {generating ? <><Spinner size={14} className="animate-spin" /> Generating…</> : <><Sparkle size={14} weight="fill" /> Generate Questions</>}
          </button>
        </div>
      </div>

      {showAdd && <AddQuestionForm onAdd={addQuestion} saving={adding} />}

      {questions.length === 0 ? (
        <div className="text-center py-14 bg-white border border-dashed border-[#E5E7EB] rounded-lg" data-testid="oq-empty">
          <Question size={40} className="mx-auto text-[#002FA7]/40 mb-3" weight="fill" />
          <p className="text-[#6B7280] font-ibm">No open questions yet. Click <span className="font-semibold text-[#002FA7]">Generate Questions</span> to have the AI build a prioritized register from your analyzed documents, or add your own.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(group => (
            <div key={group.imp} data-testid={`oq-group-${group.imp}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2 h-2 rounded-full ${IMPORTANCE_META[group.imp].dot}`} />
                <h4 className="font-ibm text-xs font-bold uppercase tracking-wider text-[#6B7280]">{IMPORTANCE_META[group.imp].label} importance ({group.items.length})</h4>
              </div>
              <div className="space-y-3">
                {group.items.map(q => (
                  <QuestionCard key={q._id} question={q} onStatus={setStatus} onDelete={remove} onSaveEdit={saveEdit} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <RequestLettersSection caseId={caseId} />
    </div>
  );
};
